import {Component, HostListener, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {NavigationStart, Router} from '@angular/router';
import {UserSessionService} from '../common/services/support/user-session/user-session.service';
import {MessageService} from '../common/services/support/message/message.service';
import {CookieService} from 'ngx-cookie-service';
import {ApplicationConstants} from '../common/constants/application.constants';
import {Subscription} from 'rxjs';
import {ViewStateService} from '../common/services/view-state/view-state.service';
import {ClaimFormNavigationService} from '../common/services/support/claim-form-navigation/claim-form-navigation.service';
import {ClaimsService} from '../common/services/data-model/app/claims/claims.service';
import {isNullOrUndefined} from '../common/utility';
import {Environment} from '../../environments/environment.interface';
import {environment} from '../../environments/environment';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class HeaderComponent implements OnInit, OnDestroy {

  /***** START - PUBLIC MEMBERS *****/
  eyefinityUrl: string;
  secureTEFUrl: string;
  ewmOptInValue: string;
  efanValue: string;
  _environment: Environment;

  claimFormPartialUrl: string = ApplicationConstants.routing.secure.claimForm + '/' + ApplicationConstants.routing.secure.claimFormEdit;
  displaySecureComponent = true;
  /***** END - PUBLIC MEMBERS *****/

  /***** START - PRIVATE MEMBERS *****/
  private observableSubscriptions: Subscription[] = [];
  private _urlNavigatingToFromClaimForm: string = undefined;
  private _isUrlNavigatingToFromClaimFormInternal: boolean = true;

  /***** END - PRIVATE MEMBERS *****/

  constructor(
    private router: Router,
    private messageService: MessageService,
    private userSessionService: UserSessionService,
    private cookieService: CookieService,
    private claimFormNavigationService: ClaimFormNavigationService,
    private claimService: ClaimsService,
    private viewStateService: ViewStateService
  ) {
    this._environment = environment;

    this.eyefinityUrl = cookieService.get(ApplicationConstants.efSurlCookie);
    this.secureTEFUrl = cookieService.get(ApplicationConstants.tefSurlCookie);
    this.ewmOptInValue = cookieService.get(ApplicationConstants.ewmOptInCookie);
    this.efanValue = cookieService.get(ApplicationConstants.acctNbrCookie);

    router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        if (!this.routerUrl.includes(this.claimFormPartialUrl)) {
          this.urlNavigatingToFromClaimFormValue = this.routerUrl;
          this.isUrlNavigatingToFromClaimFormInternalValue = true;
        }
        this.displaySecureComponent = event.url.includes('/secure');
      }
    });
  }

  /**
   * This is a host listener that monitors a specific condition on the claim form. If it is met a warning modal will be rendered prompting the user to do something.
   * @param $event - Window event the browser is listening to.
   */
  @HostListener('window:beforeunload', ['$event'])
  renderWarningPopUp($event: any) {
    if (this.shouldWarningPopUpRenderWhenTheUserIsOnTheClaimForm()) {
      $event.returnValue = true;
    }
  }

  /***** START - EVENT HANDLERS *****/
  ngOnInit() {
    // Register if save was successful after clicking the save button in the leave claim from warning modal.
    this.observableSubscriptions.push(this.viewStateService.onSaveFromLeaveClaimFormWarningModalWasSuccessful.subscribe((wasSaveSuccessful) => {
      if (!isNullOrUndefined(wasSaveSuccessful)) {
        this.navigateToDesiredUrl(this.urlNavigatingToFromClaimFormValue, this.isUrlNavigatingToFromClaimFormInternalValue);
        // This rests the observable in the autoSave logic back to its original value
        this.userSessionService.autoSavedClaimForm = undefined;
      }
    }));
  }

  ngOnDestroy() {
    this.observableSubscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }

  renderLeaveClaimFormModal(urlToNavigateTo: string, isLinkInternal: boolean) {
    this.urlNavigatingToFromClaimFormValue = urlToNavigateTo;
    this.isUrlNavigatingToFromClaimFormInternalValue = isLinkInternal;
    if (this.shouldWarningPopUpRenderWhenTheUserIsOnTheClaimForm()) {
      this.claimFormNavigationService.allowUserToLeaveClaimFormWarningModal().subscribe(allowUserToLeaveClaimForm => {
        if (allowUserToLeaveClaimForm) {
          this.navigateToDesiredUrl(urlToNavigateTo, isLinkInternal);
        }
      });
    } else {
      this.navigateToDesiredUrl(urlToNavigateTo, isLinkInternal);
    }
  }
  /***** END - EVENT HANDLERS *****/

  /***** START - PUBLIC FUNCTIONS *****/

  get routerUrl(): string {
    return this.router.url;
  }

  get urlNavigatingToFromClaimFormValue(): string {
    return this._urlNavigatingToFromClaimForm;
  }

  set urlNavigatingToFromClaimFormValue(value: string) {
    this._urlNavigatingToFromClaimForm = value;
  }

  get isUrlNavigatingToFromClaimFormInternalValue(): boolean {
    return this._isUrlNavigatingToFromClaimFormInternal;
  }

  set isUrlNavigatingToFromClaimFormInternalValue(value: boolean) {
    this._isUrlNavigatingToFromClaimFormInternal = value;
  }

  get environment(): Environment {
    return this._environment;
  }

  homeLinkClick() {
    const urlToNavigateTo = this.isOptedInToModernEyefinity() ?
                                      this.environment.modernEyefinityUrl + '/secure-ecp-central/home' :
                                      this.eyefinityUrl + '/eyefinity/html/home_page.htm';
    this.renderLeaveClaimFormModal(urlToNavigateTo, false);
  }

  getLoggedInUser() {
    return this.userSessionService.getLoggedInUser();
  }

  logoutLinkClick() {
    return this.userSessionService.logoutUser();
  }

  eClaimLinkClick() {
    const urlToNavigateTo = this.isOptedInToModernEyefinity() ?
                                    this.environment.modernEyefinityUrl + '/secure-einsurance/home' :
                                    this.eyefinityUrl + '/servlet/COREServlet?msgProc=520&pageMode=NEXTINFO';
    this.renderLeaveClaimFormModal(urlToNavigateTo, false);
  }

  governmentCommercialLinkClick() {
    const urlToNavigateTo = this.isOptedInToModernEyefinity() ?
                                    this.environment.modernEyefinityUrl + '/secure-einsurance/government-commercial/claim-tracking' :
                                    this.eyefinityUrl + '/servlet/EClaimTrkServlet?msgProc=530';
    this.renderLeaveClaimFormModal(urlToNavigateTo, false);
  }

  personalizedGreetingLinkClick() { // Conditional for modern is in the view instead
    this.renderLeaveClaimFormModal(this.environment.modernEyefinityUrl + '/secure-ecp-central/greeting', false);
  }

  accountSettingLinkClick() { // Conditional for modern is in the view instead
    this.renderLeaveClaimFormModal(this.environment.modernEyefinityUrl + '/secure-ecp-central/accountSetting', false);
  }

  changePasswordLinkClick() { // Conditional for modern is in the view instead
    this.renderLeaveClaimFormModal(this.environment.changePasswordUrl + this.getLoggedInUser(), false);
  }

  vspOnlineLinkClick() {
    this.renderLeaveClaimFormModal(this.eyefinityUrl + '/eyefinity/html/vsponlinetransfer.htm?VSPOnlineRef=eInsurance_Nav', false);
  }

  vspClaimTrackingLinkClick() {
    this.renderLeaveClaimFormModal(this.environment.applicationUrl + '/eInsurance/claims-tracking', false);
  }

  authTrackingLinkClick() {
    this.renderLeaveClaimFormModal('/secure/eInsurance/auth-tracking', true);
  }

  eLabLinkClick() {
    const urlToNavigateTo = this.isOptedInToModernEyefinity() ?
                                    this.environment.modernEyefinityUrl + '/secure-elab/home' :
                                    this.secureTEFUrl + '/elab/Home.do';
    this.renderLeaveClaimFormModal(urlToNavigateTo, false);
  }

  eBuyLinkClick() {
    this.renderLeaveClaimFormModal(this.secureTEFUrl + '/darwin/eBuyHome.do?method=eBuyHome', false);
  }

  eLearnLinkClick() {
    const urlToNavigateTo = this.isOptedInToModernEyefinity() ?
                                    this.environment.modernEyefinityUrl + '/secure-elearn/home' :
                                    this.secureTEFUrl + '/elearn/Home.do';
    this.renderLeaveClaimFormModal(urlToNavigateTo, false);
  }

  eWebLinkClick() {
    const urlToNavigateTo = this.isOptedInToModernEyefinity() ?
                                    this.environment.modernEyefinityUrl + '/secure-eweb/home' :
                                    this.secureTEFUrl + '/eweb/main';
    this.renderLeaveClaimFormModal(urlToNavigateTo, false);
  }

  eHRSolutionsLinkClick() {
    this.renderLeaveClaimFormModal(this.secureTEFUrl + '/erewards/Home.do', false);
  }

  isUserInTheClaimForm(): boolean {
    return this.routerUrl.includes(this.claimFormPartialUrl);
  }

  getPersonalizedGreeting(): string {
    return this.cookieService.get('VSPIDC_GREETING')?.split('|')[0] || '';
  }

  shouldWarningPopUpRenderWhenTheUserIsOnTheClaimForm(): boolean {
    return this.isUserInTheClaimForm() && !this.claimService.allowClaimFormToBeDeactivated;
  }

  navigateToDesiredUrl(urlToNavigateTo: string, isLinkInternal: boolean): void {
    if (isLinkInternal) {
      this.router.navigate([urlToNavigateTo]);
    } else {
      window.location.href = urlToNavigateTo;
    }
  }

  // ECLAIM-515: returns true if EWM_OPT_IN cookie value === provider's EFAN.
  isOptedInToModernEyefinity(): boolean {
    return this.ewmOptInValue != null &&
           this.ewmOptInValue.trim() !== '' &&
           this.ewmOptInValue.trim() === this.efanValue;
  }

  /***** END - PUBLIC FUNCTIONS *****/
}
